import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { UserOutlined, FileTextOutlined } from '@ant-design/icons';
import { Avatar, Button, Empty, List } from 'antd';
import ReportFiltersBar from '../../molecules/ReportFiltersBar';
import { Wrapper, CategorySection, CategoryTitle, ItemMeta, EmptyWrapper } from './presetReports.styled';
import BwgButton from '../../atoms/Button';

const ICON_BY_TYPE = {
  user: <UserOutlined />,
  file: <FileTextOutlined />,
};

function filterReports(reports, searchValue, reportTypeValue) {
  return reports.filter((report) => {
    const matchesSearch = !searchValue || report.title.toLowerCase().includes(searchValue.toLowerCase());
    const matchesType =
      !reportTypeValue || reportTypeValue === 'all' || (report.type && report.type === reportTypeValue);
    return matchesSearch && matchesType;
  });
}

function PresetReports({ searchValue, onSearchChange, reportTypes, reportTypeValue, onReportTypeChange, categories }) {
  const navigate = useNavigate();
  const filteredCategories = useMemo(
    () =>
      categories
        .map((category) => ({
          ...category,
          reports: filterReports(category.reports, searchValue, reportTypeValue),
        }))
        .filter((category) => category.reports.length > 0),
    [categories, searchValue, reportTypeValue]
  );

  return (
    <Wrapper>
      <ReportFiltersBar
        searchPlaceholder="Pesquisar por relatórios"
        reportTypePlaceholder="Tipo de Relatórios"
        searchValue={searchValue}
        onSearchChange={onSearchChange}
        reportTypes={reportTypes}
        reportTypeValue={reportTypeValue}
        onReportTypeChange={onReportTypeChange}
      />

      {filteredCategories.map((category) => (
        <CategorySection key={category.id}>
          <CategoryTitle>{category.name}</CategoryTitle>
          <List
            itemLayout="horizontal"
            dataSource={category.reports}
            renderItem={(report) => (
              <List.Item
                extra={
                  <Button
                    type="primary"
                    onClick={() => (report.path ? navigate(report.path) : report.onGenerate?.(report))}
                  >
                    Gerar
                  </Button>
                }
              >
                <ItemMeta
                  avatar={
                    <Avatar
                      style={{ backgroundColor: '#E6FFFB', color: '#13C2C2' }}
                      icon={ICON_BY_TYPE[report.icon]}
                    />
                  }
                  title={report.title}
                  description={report.description}
                />
              </List.Item>
            )}
          />
        </CategorySection>
      ))}
      {filteredCategories.length === 0 && (
        <EmptyWrapper>
          <Empty description="Nenhum relatório encontrado" />
          <BwgButton
            type="primary"
            onClick={() => {
              onSearchChange('');
              onReportTypeChange(undefined);
            }}
          >
            {' '}
            Limpar Pesquisa
          </BwgButton>
        </EmptyWrapper>
      )}
    </Wrapper>
  );
}

const reportShape = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  type: PropTypes.string,
  path: PropTypes.string,
  icon: PropTypes.oneOfType([PropTypes.oneOf(['user', 'file']), PropTypes.elementType]),
  onGenerate: PropTypes.func,
});

const categoryShape = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  name: PropTypes.string.isRequired,
  reports: PropTypes.arrayOf(reportShape).isRequired,
});

PresetReports.propTypes = {
  searchValue: PropTypes.string,
  onSearchChange: PropTypes.func.isRequired,
  reportTypes: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired,
    })
  ),
  reportTypeValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onReportTypeChange: PropTypes.func.isRequired,
  categories: PropTypes.arrayOf(categoryShape).isRequired,
};

PresetReports.defaultProps = {
  searchValue: '',
  reportTypes: [
    { value: 'all', label: 'Todos' },
    { value: 'cooperados', label: 'Cooperados' },
    { value: 'gestao', label: 'Gestão e Apontamentos' },
  ],
  reportTypeValue: undefined,
};

export default PresetReports;
